'use client';

import NavFooterLayout from 'src/components/Layouts/NavFooterLayout';
import PageContentWrapper from 'src/components/Layouts/PageContentWrapper';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import ScrollShadows from 'src/components/ScrollShadows';
import useLiquidationStats from 'src/hooks/Liquidations/useLiquidationStats';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import useGoToRoute from 'src/hooks/useGoToRoute';
import StatsOverviewSection from 'src/components/Stats/StatsOverviewSection';

type StatsPageType =
	| 'funding-history'
	| 'funding-comparison'
	| 'liquidations'
	| 'bankruptcies'
	| 'stats';

const tabs = [
	{
		value: 'funding-history' as const,
		label: 'Funding History',
	},
	{
		value: 'funding-comparison' as const,
		label: 'Funding Comparison',
	},
	{
		value: 'liquidations' as const,
		label: 'Liquidations',
	},
	{
		value: 'bankruptcies' as const,
		label: 'Bankruptcies',
	},
];

const StatsPageLayout = (props: { children: React.ReactNode }) => {
	const goToRoute = useGoToRoute();
	const isMobile = useIsMobileScreenSize();
	const routerPath = usePathname();

	const scrollableTabsRef = useRef<HTMLDivElement>(null);

	const routerSubPath =
		routerPath?.split('?')?.[0]?.replace('/stats', '')?.replace('/', '') ?? '';
	const selectedTab = useMemo(
		() =>
			routerSubPath ? (routerSubPath as StatsPageType) : 'funding-history',
		[routerSubPath]
	);

	useLiquidationStats();

	const handleRouteChange = (route: StatsPageType) => {
		const newRoute = `/stats/${route}`;
		goToRoute(newRoute);
	};

	useEffect(() => {
		if (routerPath === '/stats') {
			goToRoute('/stats/funding-history');
		}
	}, [routerPath]);

	return (
		<NavFooterLayout overflowAuto>
			{isMobile ? (
				<>
					<div className="sticky top-0 z-100 flex w-full flex-col bg-main-bg">
						<div className="flex flex-col px-md">
							<span className="w-full p-4 text-center typo-h3 text-text-default">
								Stats
							</span>
							<div className="flex w-full justify-center max-sm:mb-md">
								<StatsOverviewSection />
							</div>
						</div>

						<ScrollShadows
							scrollableRef={scrollableTabsRef}
							gradientFromClassName="from-main-bg"
						>
							<div
								ref={scrollableTabsRef}
								className="hidden-scroll w-full items-center overflow-auto border-b border-stroke-divider bg-main-bg px-2"
							>
								<Tabs
									options={tabs}
									activeTab={selectedTab}
									onTabChange={handleRouteChange}
								/>
							</div>
						</ScrollShadows>
					</div>
					<div className="hidden-scroll flex grow overflow-x-hidden overflow-y-auto text-text-secondary">
						{props.children}
					</div>
				</>
			) : (
				<PageContentWrapper maxWidth={1328}>
					<div className="flex h-full w-full flex-col gap-y-md sm:gap-y-lg">
						<span className="py-2 text-center typo-h3 text-text-default">
							Stats
						</span>
						<div className="flex w-full justify-center">
							<StatsOverviewSection />
						</div>
						<div className="flex w-full items-center justify-center border-b border-stroke-divider">
							<div className="inline-flex justify-self-start lg:max-w-[70%]">
								<Tabs
									options={tabs}
									activeTab={selectedTab}
									onTabChange={handleRouteChange}
								/>
							</div>
						</div>
						<div className="min-h-[400px]">{props.children}</div>
					</div>
				</PageContentWrapper>
			)}
		</NavFooterLayout>
	);
};

export default StatsPageLayout;
